import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useRouteMatch } from "react-router-dom";
import CachedIcon from '@mui/icons-material/Cached';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { ReactComponent as MySvgFile } from '../../../Images/logs.svg';
import LogTrigger from '../../../LogTrigger';
import { Button, Grid, Skeleton, IconButton, ButtonGroup, Fade } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { formatSummaryToJSX } from './formatSummary';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Close } from '@mui/icons-material';
import { CONFIG_APP_UPTIME_METRICS_BASE_URL } from '../../../configApp';
import { AnimatePresence, motion } from "framer-motion/dist/framer-motion";
import { CustomTooltip } from '../../Settings/Organization';

export const LOGS = gql`
  query Logs($appName: String!, $region: String) {
    getclusterLog(appName: $appName, region: $region)
  }
`;

export const APP_REGION = gql`
  query AppQuery($name: String!) {
    app(name: $name) {
      id
      status
      regions {
        code
        name
      }
    }
  }
`;

const blinkStyle = {
  height: "10px",
  width: "10px",
  backgroundColor: "#4caf50",
  borderRadius: "50%",
  display: "inline-block",
  marginRight: "8px",
  animation: "blinker 1.2s linear infinite"
};

const keyframesStyle = `
@keyframes blinker {
  50% { opacity: 0; }
}
@keyframes dot {
  0%, 20% { content: "."; }
  40% { content: ".."; }
  60% { content: "..."; }
  100% { content: ""; }
}
.dot-anim::after {
  content: "";
  animation: dot 1.4s steps(4, end) infinite;
}`;

if (typeof document !== "undefined" && !document.getElementById("blinker-style")) {
  const style = document.createElement("style");
  style.id = "blinker-style";
  style.innerHTML = keyframesStyle;
  document.head.appendChild(style);
}

export default function FolderList({ isDatabase }) {
  const match = useRouteMatch();
  const [summary, setSummary] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const logContainerRef = useRef(null);

  const { loading: appload, error: apperr, data: appdata } = useQuery(APP_REGION, {
    variables: { name: match.params.id },
    onCompleted: () => {
      getClusters();
    }
  });

  const [getClusters, { loading, error, data }] = useLazyQuery(LOGS, {
    variables: {
      appName: match.params.id,
      region: appdata?.app?.regions?.[0]?.code || ""
    },
    onCompleted: () => {
      setLastFetched(new Date());
    }
  });

  const handleSummarize = async () => {
    if (summary) {
      setShowSummary(prev => !prev);
      return;
    }
    setLoadingSummary(true);
    setShowSummary(true);
    try {
      const res = await axios.post(`${CONFIG_APP_UPTIME_METRICS_BASE_URL}/analyze-ai-log`, {
        log: data?.getclusterLog || ''
      });
      setSummary(res.data.log_summary || 'No summary received.');
    } catch (err) {
      setSummary('Failed to summarize logs.');
      console.error(err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const scrollToTop = () => {
    logContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    logContainerRef.current?.scrollTo({ top: logContainerRef.current.scrollHeight, behavior: 'smooth' });
  };

  if (loading || appload) return "Loading...";
  if (error) LogTrigger(error.message, "error", window.location.href, "App Log");
  if (data) LogTrigger("App log loaded", "info", window.location.href, "App Log");

  if (appdata?.app?.status === "Suspended") {
    return <Typography sx={{ m: 3, fontWeight: "bold" }}>App is Suspended, Cannot show logs.</Typography>;
  }

  return (
    <div>
      {data?.getclusterLog ? (
        <div>
          <Grid container justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              {isDatabase ? "Database" : "App"} Logs
            </Typography>
            <Button
              variant="outlined"
              onClick={handleSummarize}
              disabled={loadingSummary}
              startIcon={loadingSummary ? <Close /> : <AutoAwesomeIcon />}
            >
              {loadingSummary ? (
                "Summarizing..."
              ) : summary ? (showSummary ? "Hide Summary" : "Show Summary") : "AI Summarize Logs"}
            </Button> 
          </Grid>

          {summary && showSummary && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Fade in={showSummary} timeout={500}>
              {formatSummaryToJSX(summary)}
              </Fade>
            </Paper>
          )}

          {loadingSummary && (
             <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                 <CustomTooltip title="Nife AI (Nia) is thinking about the logs and analyzing them for insights.">
         
      <Grid container alignItems="center" justifyContent="start">
        <Grid item>
          <motion.div
            style={{
              width: "54px",
              height: "54px",
              marginRight: "8px",
              borderRadius: "50%",
              boxShadow: "0 0 12px rgba(100, 200, 255, 0.5)",
              overflow: "hidden",
              position: "relative",
              background: "radial-gradient(circle at 30% 30%, #cce6ff, #003366)",
            }}
            animate={{
              y: [0, -3, 0],
              rotate: [-2, 2, -2],
              boxShadow: [
                "0 0 8px rgba(100, 200, 255, 0.3)",
                "0 0 12px rgba(100, 200, 255, 0.8)",
                "0 0 8px rgba(100, 200, 255, 0.3)",
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <motion.img
              src="/nia.jpg"
              alt="Nia Thinking"
              style={{
                
                width: "100%",
                height: "100%",
                objectFit: "cover",
           
              }}
              initial={{ opacity: 0.9 }}
              animate={{
                opacity: [0.9, 1, 0.95],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
              }}
            />
        
          </motion.div>
        </Grid>
        <Grid item>
        <span
            style={{
              display: "inline-block",
              fontStyle: "italic",
              fontSize: "18px",
              color: "#888",
              fontWeight: "bold",
            }}
          >
            Nia is Thinking<span className="dot-anim">.</span>
          </span>
        </Grid>
      </Grid>
      </CustomTooltip>
      <Skeleton variant="text" width="80%" />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="40%" />
    </Paper>
          )}

          <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <span style={blinkStyle}></span>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Live
                {lastFetched && (
                  <span style={{ marginLeft: "12px", fontSize: "12px", color: "#888" }}>
                    (Last fetched: {lastFetched.toLocaleString(undefined, {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })})
                  </span>
                )}
              </Typography>
            </Grid>
            <Grid item>
              <ButtonGroup variant="text" size="small">
                <Button onClick={scrollToTop}>Top</Button>
                <Button onClick={scrollToBottom}>Bottom</Button>
              </ButtonGroup>
            </Grid>
          </Grid>

          <Paper
            ref={logContainerRef}
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: "#0d0d0d",
              color: "#ffffff",
              overflowY: "auto",
              maxHeight: "500px",
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              borderRadius: "8px"
            }}
          >
            {loading && <Skeleton height={10} />}
            <div style={{ display: "flex", flexDirection: "column" }}>
              {data.getclusterLog.split("\n").map((line, index) => {
                const isError = /error/i.test(line);
                const isWarn = /warn/i.test(line);
                const isInfo = /info/i.test(line);
                const isDebug = /debug/i.test(line);

                let color = "#e0e0e0";
                if (isError) color = "#f44336";
                else if (isWarn) color = "#ff9800";
                else if (isInfo) color = "#2196f3";
                else if (isDebug) color = "#9c27b0";

                return (
                  <div key={index} style={{ display: "flex", alignItems: "flex-start", lineHeight: "1.5" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "50px",
                        textAlign: "right",
                        paddingRight: "12px",
                        marginRight: "12px",
                        color: "#888",
                        userSelect: "none",
                        borderRight: "1px solid #444"
                      }}
                    >
                      {String(index + 1).padStart(4, " ")}
                    </span>
                    <span style={{ color }}>{line}</span>
                  </div>
                );
              })}
            </div>
          </Paper>
        </div>
      ) : (
        <div style={{ padding: "0px" }}>
          <h3 style={{ marginBottom: "0px" }}>No data</h3>
          <div style={{ color: "grey" }}>No logs to show</div>
          <MySvgFile style={{ width: "100%", height: "100%", minWidth: "100px", marginTop: "5px", maxWidth: "600px" }} />
        </div>
      )}
    </div>
  );
}
